import * as Dialog from '@radix-ui/react-dialog'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  /** Renders the confirm button in the danger colour. Use for deletes. */
  destructive?: boolean
  loading?: boolean
  /**
   * Why the action was refused, shown above the buttons with the dialog left open.
   *
   * <p>A confirmation is the one place a server's refusal has to land. Half the destructive
   * actions in this product are allowed to say no — a bank account salary is paid into, a
   * department that still has people in it, an employee with a pay history — and the reason
   * is the useful part of the answer. A dialog that closes and raises a toast has thrown it
   * away by the time somebody looks up.
   */
  error?: string | null
  onConfirm: () => void
}

/** Confirmation prompt used before any destructive or irreversible action. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive,
  loading,
  error,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(28rem,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-card)] bg-[var(--color-card)] p-6 shadow-[var(--shadow-popover)]">
          <div className="flex items-start gap-3">
            {destructive && (
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--color-tint-red)]">
                <AlertTriangle className="size-5 text-[var(--color-danger)]" aria-hidden="true" />
              </span>
            )}
            <div className="min-w-0">
              <Dialog.Title className="text-base font-semibold text-[var(--color-heading)]">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-[var(--color-body)]">
                  {description}
                </Dialog.Description>
              )}
            </div>
          </div>

          {error && (
            <p role="alert" className="mt-4 text-sm text-[var(--color-danger)]">{error}</p>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild>
              <Button variant="outline" disabled={loading}>
                {cancelLabel}
              </Button>
            </Dialog.Close>
            <Button
              variant={destructive ? 'danger' : 'primary'}
              onClick={onConfirm}
              disabled={loading}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
